import { useCallback, useState } from 'react';
import {
  addToSelection,
  clearSection,
  SelectionRange,
  selectRange,
  selectSingleCell,
} from '@/shared/ui/data/EditableGrid/lib/selection';
import { Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import { includesPosition } from '@/shared/ui/data/EditableGrid/lib/position';

interface IOptions {
  onSelectionChange?: (positions: ReadonlyArray<Position>) => void;
}

export function useCellSelection(options: IOptions = {}) {
  const [selectedCells, setSelectedCells] = useState<ReadonlyArray<Position>>([]);
  const [selectionAnchor, setSelectionAnchor] = useState<Position | null>(null);

  const selectCell = useCallback(
    (position: Position) => {
      const newSelection = selectSingleCell(position);
      setSelectedCells(newSelection);
      setSelectionAnchor(position);
      options.onSelectionChange?.(newSelection);
    },
    [options]
  );

  const selectCellRange = useCallback(
    (endPosition: Position) => {
      if (!selectionAnchor) {
        selectCell(endPosition);
        return;
      }

      const range: SelectionRange = {
        start: selectionAnchor,
        end: endPosition,
      };

      const newSelection = selectRange(range);
      setSelectedCells(newSelection);
      options.onSelectionChange?.(newSelection);
    },
    [selectionAnchor, selectCell, options]
  );

  const toggleCellSelection = useCallback(
    (position: Position) => {
      const newSelection = addToSelection(selectedCells, position);
      setSelectedCells(newSelection);
      options.onSelectionChange?.(newSelection);
    },
    [selectedCells, options]
  );

  const deselectAll = useCallback(() => {
    const newSelection = clearSection();
    setSelectedCells(newSelection);
    setSelectionAnchor(null);
    options.onSelectionChange?.(newSelection);
  }, [options]);

  const isSelected = useCallback(
    (position: Position): boolean => {
      return includesPosition(selectedCells, position);
    },
    [selectedCells]
  );

  return {
    selectedCells,
    selectCell,
    selectCellRange,
    toggleCellSelection,
    deselectAll,
    isSelected,
  };
}
